// api/staff.ts — typed wrappers for /api/staff/* endpoints.
//
// W6 v1 Session 1.

import { staffApiJson, staffApiUpload, staffApiFetch, StaffApiError, setStaffToken, getStaffToken } from './staff-client';
import type { RecoveryKit } from './auth';

export type StaffRole = 'titolare' | 'instructor_teoria' | 'instructor_guida' | 'segreteria';

export interface StaffMe {
  school_staff_id: string;
  school_id:       string;
  role:            StaffRole;
  first_name:      string;
  last_name:       string | null;
  email:           string;
  school_name:     string | null;
}

export interface StaffProfFeedbackRow {
  message_id: string;
  student_id: string;
  session_id: string;
  signal: 'useful' | 'confusing';
  engine: string;
  model_id: string;
  pack_id: string | null;
  guardrail_outcome: string;
  updated_at: string;
  student_first_name: string;
  response_text: string;
  response_created_at: string;
}

export interface RosterStudent {
  id:           string;
  first_name:   string;
  age_bracket:  'under_18' | 'adult';
  exam_date:    string | null;
  cohort_id:    string | null;
  cohort_name:  string | null;
  enrolled_at:  string;
  paused:       boolean;
}

export interface InviteCode {
  code:           string;
  cohort_id:      string | null;
  cohort_name:    string | null;
  max_uses:       number;
  use_count:      number;
  purpose:        'student' | 'staff';
  intended_role:  StaffRole | null;
  notes:          string | null;
  expires_at:     string | null;
  created_at:     string;
}

export interface AttendanceRow {
  id:               string;
  student_id:       string;
  date:             string;
  kind:             'lezione_teoria' | 'guida' | 'simulazione_in_aula';
  notes:            string | null;
  created_at:       string;
  school_staff_id?: string | null;
}

export interface Cohort {
  id:         string;
  name:       string;
  ab_arm:     string | null;
  created_at: string;
}

export interface ExchangeStaffInviteCodeResponse {
  token:         string;
  schoolStaffId: string;
  schoolId:      string;
  role:          StaffRole;
  firstName:     string;
  lastName:      string | null;
  email:         string;
  expiresAt:     number;
}

export const staffAuthApi = {
  async exchangeInviteCode(body: {
    code: string;
    firstName: string;
    lastName?: string;
    email: string;
  }): Promise<ExchangeStaffInviteCodeResponse> {
    const res = await staffApiJson<ExchangeStaffInviteCodeResponse>('/api/staff/auth/exchange-invite-code', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    setStaffToken(res.token);
    return res;
  },

  async logout(): Promise<boolean> {
    const token = getStaffToken();
    const response = await staffApiFetch('/api/staff/auth/logout', { method: 'POST' });
    let body: unknown;
    try { body = await response.json(); } catch { body = null; }
    const revoked = response.ok && typeof body === 'object' && body !== null
      && 'revoked' in body && body.revoked === true;
    if (!revoked && response.status !== 401) {
      throw new StaffApiError(response.status, body, 'Uscita non completata. Riprovi.');
    }
    // A late logout response must not erase a newer sign-in.
    if (getStaffToken() !== token) return false;
    setStaffToken(null);
    return true;
  },
};

export const staffApi = {
  async me(): Promise<StaffMe> {
    return staffApiJson<StaffMe>('/api/staff/me');
  },

  async listProfFeedback(signal: 'useful' | 'confusing' = 'confusing'): Promise<{ feedback: StaffProfFeedbackRow[] }> {
    return staffApiJson<{ feedback: StaffProfFeedbackRow[] }>(`/api/staff/prof-feedback?signal=${signal}`);
  },

  async listStudents(): Promise<{ students: RosterStudent[] }> {
    return staffApiJson<{ students: RosterStudent[] }>('/api/staff/students');
  },

  async createStudent(body: {
    first_name: string;
    age_bracket: 'under_18' | 'adult';
    cohort_id?: string | null;
    exam_date?: string | null;
  }): Promise<{ student: { id: string; first_name: string; cohort_id: string | null; exam_date: string | null; enrolled_at: string } }> {
    return staffApiJson('/api/staff/students', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async updateStudent(id: string, body: {
    cohort_id?: string | null;
    exam_date?: string | null;
    paused?: boolean;
  }): Promise<{ student: { id: string; first_name: string; cohort_id: string | null; exam_date: string | null; paused: boolean } }> {
    return staffApiJson(`/api/staff/students/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  async rotateStudentRecoveryKit(studentId: string): Promise<RecoveryKit> {
    return staffApiJson<RecoveryKit>(
      `/api/staff/students/${encodeURIComponent(studentId)}/recovery-kit`,
      { method: 'POST' },
    );
  },

  async listCohorts(): Promise<{ cohorts: Cohort[] }> {
    return staffApiJson<{ cohorts: Cohort[] }>('/api/staff/cohorts');
  },

  async listInviteCodes(): Promise<{ invite_codes: InviteCode[] }> {
    return staffApiJson<{ invite_codes: InviteCode[] }>('/api/staff/invite-codes');
  },

  async createInviteCode(body: {
    purpose?: 'student' | 'staff';
    cohort_id?: string | null;
    max_uses?: number;
    expires_at?: string | null;
    notes?: string | null;
    intended_role?: StaffRole;
  }): Promise<{ invite_code: InviteCode }> {
    return staffApiJson<{ invite_code: InviteCode }>('/api/staff/invite-codes', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async revokeInviteCode(code: string): Promise<{ revoked: boolean; code: string }> {
    return staffApiJson(`/api/staff/invite-codes/${encodeURIComponent(code)}/revoke`, {
      method: 'POST',
    });
  },

  async listAttendance(studentId: string): Promise<{ attendance: AttendanceRow[] }> {
    return staffApiJson<{ attendance: AttendanceRow[] }>(`/api/staff/students/${encodeURIComponent(studentId)}/attendance`);
  },

  async recordAttendance(studentId: string, body: {
    date: string;
    kind: 'lezione_teoria' | 'guida' | 'simulazione_in_aula';
    notes?: string;
  }): Promise<{ attendance: AttendanceRow }> {
    return staffApiJson<{ attendance: AttendanceRow }>(`/api/staff/students/${encodeURIComponent(studentId)}/attendance`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // -------------------------------------------------------------------------
  // W6 v1 Session 2 — Instructor / Titolare dashboard + per-student detail
  // -------------------------------------------------------------------------
  // None of these are accessible to segreteria; the BE role gate returns 403.

  async dashboardHome(): Promise<DashboardHomePayload> {
    return staffApiJson<DashboardHomePayload>('/api/staff/dashboard/home');
  },

  async studentProgresso(studentId: string): Promise<StudentProgressoPayload> {
    return staffApiJson<StudentProgressoPayload>(
      `/api/staff/students/${encodeURIComponent(studentId)}/progresso`,
    );
  },

  // W6 v1.1 Session 3 (item 12) — 30-day Indice trend chart on Tab 1.
  async studentIndiceTrend(
    studentId: string,
    opts?: { days?: number },
  ): Promise<IndiceTrendPayload> {
    const days = opts?.days ?? 30;
    return staffApiJson<IndiceTrendPayload>(
      `/api/staff/students/${encodeURIComponent(studentId)}/indice-trend?days=${days}`,
    );
  },

  async studentBrain(studentId: string): Promise<StudentBrainPayload> {
    return staffApiJson<StudentBrainPayload>(
      `/api/staff/students/${encodeURIComponent(studentId)}/brain`,
    );
  },

  async studentAttivita(
    studentId: string,
    opts?: { page?: number; pageSize?: number },
  ): Promise<StudentAttivitaPayload> {
    const qs = new URLSearchParams();
    if (opts?.page)     qs.set('page',     String(opts.page));
    if (opts?.pageSize) qs.set('pageSize', String(opts.pageSize));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return staffApiJson<StudentAttivitaPayload>(
      `/api/staff/students/${encodeURIComponent(studentId)}/attivita${tail}`,
    );
  },

  async studentAttivitaSession(
    studentId: string,
    sessionId: string,
  ): Promise<StudentAttivitaSessionPayload> {
    return staffApiJson<StudentAttivitaSessionPayload>(
      `/api/staff/students/${encodeURIComponent(studentId)}/attivita/${encodeURIComponent(sessionId)}`,
    );
  },

  // -------------------------------------------------------------------------
  // W6 v1 Session 3 — Content gap queue (spec §5.4)
  // -------------------------------------------------------------------------

  async listGapQueue(opts?: {
    statuses?: Array<'open' | 'in_review' | 'resolved' | 'archived'>;
    studentId?: string;
    page?: number;
    pageSize?: number;
  }): Promise<GapQueuePayload> {
    const qs = new URLSearchParams();
    if (opts?.statuses && opts.statuses.length > 0) qs.set('status', opts.statuses.join(','));
    if (opts?.studentId) qs.set('student', opts.studentId);
    if (opts?.page)      qs.set('page',     String(opts.page));
    if (opts?.pageSize)  qs.set('pageSize', String(opts.pageSize));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return staffApiJson<GapQueuePayload>(`/api/staff/gap-queue${tail}`);
  },

  async respondToGap(gapId: string, body: { response_text: string }): Promise<{ gap: GapQueueRow }> {
    return staffApiJson<{ gap: GapQueueRow }>(
      `/api/staff/gap-queue/${encodeURIComponent(gapId)}/respond`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  async escalateGap(gapId: string, body?: { notes?: string }): Promise<{ gap: GapQueueRow }> {
    return staffApiJson<{ gap: GapQueueRow }>(
      `/api/staff/gap-queue/${encodeURIComponent(gapId)}/escalate`,
      { method: 'POST', body: JSON.stringify(body ?? {}) },
    );
  },

  async archiveGap(gapId: string): Promise<{ gap: GapQueueRow }> {
    return staffApiJson<{ gap: GapQueueRow }>(
      `/api/staff/gap-queue/${encodeURIComponent(gapId)}/archive`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  },

  // -------------------------------------------------------------------------
  // W6 v1 Session 3 — Read-only metrics (spec §7.2.12 + §8.4 #13 + §8.1.5)
  // -------------------------------------------------------------------------

  async metricsRefusalRate(days = 30): Promise<RefusalRatePayload> {
    return staffApiJson<RefusalRatePayload>(`/api/staff/metrics/refusal-rate?days=${days}`);
  },

  async metricsCostPerStudent(days = 30): Promise<CostPerStudentPayload> {
    return staffApiJson<CostPerStudentPayload>(`/api/staff/metrics/cost-per-student?days=${days}`);
  },

  async metricsInstructorTime(days = 30): Promise<InstructorTimePayload> {
    return staffApiJson<InstructorTimePayload>(`/api/staff/metrics/instructor-time?days=${days}`);
  },

  // -------------------------------------------------------------------------
  // W6 v1.1 — Class pulse (spec §5.5)
  // -------------------------------------------------------------------------

  async pulse(period: '7d' | '30d' | '90d' = '30d'): Promise<PulsePayload> {
    return staffApiJson<PulsePayload>(`/api/staff/pulse?period=${period}`);
  },

  // -------------------------------------------------------------------------
  // W6 v1.1 — Alerts (spec §5.6)
  // -------------------------------------------------------------------------

  async alertsRecent(opts?: { kinds?: AlertKind[]; hours?: number }): Promise<AlertsRecentPayload> {
    const qs = new URLSearchParams();
    if (opts?.kinds && opts.kinds.length > 0) qs.set('kinds', opts.kinds.join(','));
    if (opts?.hours) qs.set('hours', String(opts.hours));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return staffApiJson<AlertsRecentPayload>(`/api/staff/alerts/recent${tail}`);
  },

  async dismissAlert(alertStateId: string): Promise<{ dismissed: boolean; id: string }> {
    return staffApiJson<{ dismissed: boolean; id: string }>(
      `/api/staff/alerts/${encodeURIComponent(alertStateId)}/dismiss`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  },

  // -------------------------------------------------------------------------
  // W6 v1.1 Session 2 — Tab 4 Azioni (spec §5.3.4)
  // -------------------------------------------------------------------------

  async sendStudentMessage(studentId: string, body_md: string): Promise<{ message: StudentMessageRow }> {
    return staffApiJson<{ message: StudentMessageRow }>(
      `/api/staff/students/${encodeURIComponent(studentId)}/message`,
      { method: 'POST', body: JSON.stringify({ body_md }) },
    );
  },

  async flagOneOnOne(studentId: string, note?: string): Promise<{ flag: StudentMessageRow }> {
    return staffApiJson<{ flag: StudentMessageRow }>(
      `/api/staff/students/${encodeURIComponent(studentId)}/flag`,
      { method: 'POST', body: JSON.stringify({ note: note ?? '' }) },
    );
  },

  async pauseStudent(studentId: string, reason?: string): Promise<PauseStudentResponse> {
    return staffApiJson<PauseStudentResponse>(
      `/api/staff/students/${encodeURIComponent(studentId)}/pause`,
      { method: 'POST', body: JSON.stringify({ reason: reason ?? '' }) },
    );
  },

  async unpauseStudent(studentId: string): Promise<PauseStudentResponse> {
    return staffApiJson<PauseStudentResponse>(
      `/api/staff/students/${encodeURIComponent(studentId)}/unpause`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  },

  // -------------------------------------------------------------------------
  // W6 v1.1 Session 2 — Tab 2 Brain WRITE side (spec §5.3 Tab 2)
  // -------------------------------------------------------------------------

  async addBrainNote(studentId: string, body: { markdown: string; supersedes_id?: string }): Promise<{ note: BrainNoteWriteResult }> {
    return staffApiJson<{ note: BrainNoteWriteResult }>(
      `/api/staff/students/${encodeURIComponent(studentId)}/brain/notes`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  async deleteBrainNote(studentId: string, noteId: string): Promise<{ deleted: boolean; id: string; already_deleted?: boolean }> {
    return staffApiJson<{ deleted: boolean; id: string; already_deleted?: boolean }>(
      `/api/staff/students/${encodeURIComponent(studentId)}/brain/notes/${encodeURIComponent(noteId)}`,
      { method: 'DELETE' },
    );
  },

  // -------------------------------------------------------------------------
  // W7 Session 2 — corpus drafts review surface (spec §7 + ROADMAP §W7 task 4)
  // -------------------------------------------------------------------------
  // Endpoints under /api/corpus/drafts/* — instructor + titolare only;
  // segreteria gets 403 from the BE role gate.

  async listDrafts(opts?: {
    status?: DraftStatusFilter;
    limit?: number;
    offset?: number;
  }): Promise<DraftListPayload> {
    const qs = new URLSearchParams();
    if (opts?.status) qs.set('status', opts.status);
    if (opts?.limit  != null) qs.set('limit',  String(opts.limit));
    if (opts?.offset != null) qs.set('offset', String(opts.offset));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return staffApiJson<DraftListPayload>(`/api/corpus/drafts${tail}`);
  },

  async draftPendingCount(): Promise<{ pending_count: number }> {
    return staffApiJson<{ pending_count: number }>(`/api/corpus/drafts/pending-count`);
  },

  async getDraft(draftId: string): Promise<DraftDetailPayload> {
    return staffApiJson<DraftDetailPayload>(`/api/corpus/drafts/${encodeURIComponent(draftId)}`);
  },

  async approveDraft(
    draftId: string,
    body: { edited_proposed_additions?: string; review_notes?: string } = {},
  ): Promise<DraftApprovePayload> {
    return staffApiJson<DraftApprovePayload>(
      `/api/corpus/drafts/${encodeURIComponent(draftId)}/approve`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  async rejectDraft(
    draftId: string,
    body: { review_notes?: string } = {},
  ): Promise<DraftRejectPayload> {
    return staffApiJson<DraftRejectPayload>(
      `/api/corpus/drafts/${encodeURIComponent(draftId)}/reject`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  // -------------------------------------------------------------------------
  // W7 Session 3 — gap-queue → draft flow + KB picker for upload widget
  // -------------------------------------------------------------------------
  // Endpoints under /api/corpus/research/from-gap and /api/corpus/kb/* —
  // instructor + titolare only; segreteria gets 403 from the BE role gate.

  async researchFromGap(body: { gap_id: string }): Promise<CorpusResearchFromGapPayload> {
    return staffApiJson<CorpusResearchFromGapPayload>(
      '/api/corpus/research/from-gap',
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  async listCorpusArgomenti(): Promise<{ argomenti: ArgomentoPickerRow[] }> {
    return staffApiJson<{ argomenti: ArgomentoPickerRow[] }>(
      '/api/corpus/kb/argomenti',
    );
  },

  async listCorpusPrinciplesForArgomento(
    argomentoId: string,
  ): Promise<{ principles: PrinciplePickerRow[] }> {
    return staffApiJson<{ principles: PrinciplePickerRow[] }>(
      `/api/corpus/kb/argomenti/${encodeURIComponent(argomentoId)}/principles`,
    );
  },

  async uploadCorpusDocument(file: File): Promise<CorpusUploadPayload> {
    const fd = new FormData();
    fd.append('file', file);
    // staff-client serializes JSON by default; the multipart path needs
    // the FormData to flow through fetch. staffApiUpload uses the raw
    // client + setStaffToken just like staffApiJson but skips the JSON
    // content-type header.
    return staffApiUpload<CorpusUploadPayload>('/api/corpus/upload', fd);
  },

  async researchFromDocument(body: {
    source_document_id: string;
    principle_id: string;
  }): Promise<CorpusResearchPayload> {
    return staffApiJson<CorpusResearchPayload>('/api/corpus/research', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
};

// ---------------------------------------------------------------------------
// W7 Session 2 — corpus drafts payload types
// ---------------------------------------------------------------------------

export type DraftStatus = 'pending' | 'approved' | 'rejected' | 'superseded';
export type DraftStatusFilter = DraftStatus;

export interface DraftPrincipleSummary {
  id:           string;
  name_it:      string;
  enunciato:    string;
  argomento_id: string;
  citation:     string;  // e.g. "[arg13 · P5]"
}

export interface DraftSourceDocumentSummary {
  id:       string;
  filename: string;
}

export interface DraftListItem {
  id:                          string;
  status:                      DraftStatus;
  principle_id:                string | null;
  principle:                   DraftPrincipleSummary | null;
  source_document:             DraftSourceDocumentSummary | null;
  proposed_additions:          string;
  citations:                   unknown[];
  provenance:                  Record<string, unknown>;
  created_at:                  string;
  created_by_kind:             'system' | 'gap_queue';
  created_by_school_staff_id:  string;
  reviewed_by_school_staff_id: string | null;
  reviewed_at:                 string | null;
}

export interface DraftListPayload {
  drafts:        DraftListItem[];
  pending_count: number;
  total_count:   number;
}

export interface DraftDetailPayload {
  draft: {
    id:                          string;
    status:                      DraftStatus;
    principle_id:                string | null;
    source_document_id:          string | null;
    proposed_additions:          string;
    citations:                   unknown[];
    provenance:                  Record<string, unknown>;
    created_at:                  string;
    created_by_kind:             'system' | 'gap_queue';
    created_by_school_staff_id:  string;
    reviewed_by_school_staff_id: string | null;
    reviewed_at:                 string | null;
    review_notes:                string | null;
  };
  principle: DraftPrincipleSummary | null;
  source_document: {
    id:         string;
    filename:   string;
    signed_url: string | null;
  } | null;
}

export interface DraftApprovePayload {
  draft_id:         string;
  new_principle_id: string;
  supersedes:       string;
}

export interface DraftRejectPayload {
  draft_id: string;
  status:   'rejected';
}

// ---------------------------------------------------------------------------
// W7 Session 3 — corpus from-gap + upload + KB picker payload types
// ---------------------------------------------------------------------------

/** Response from POST /api/corpus/research/from-gap. Mirrors Session 1's
 *  research response shape so the FE can navigate directly to the new
 *  draft in /staff/bozze without a refetch. */
export interface CorpusResearchFromGapPayload {
  draft: {
    id:                  string;
    status:              'pending';
    proposed_additions:  string;
    citations:           unknown[];
    provenance:          Record<string, unknown>;
    principle_id:        string;
    /** Always null on the from-gap path. */
    source_document_id:  null;
  };
  principle: {
    id:   string;
    text: string;
  };
  gap: {
    id:       string;
    question: string;
  };
}

/** Response from POST /api/corpus/upload. Mirrors the BE
 *  `CorpusUploadResponse` shape. */
export interface CorpusUploadPayload {
  source_document_id: string;
  signed_url:         string | null;
  filename:           string;
  byte_size:          number;
}

/** Response from POST /api/corpus/research. Mirrors Session 1's BE
 *  `CorpusResearchResponse` shape. */
export interface CorpusResearchPayload {
  draft: {
    id:                  string;
    status:              'pending';
    proposed_additions:  string;
    citations:           unknown[];
    provenance:          Record<string, unknown>;
    principle_id:        string;
    source_document_id:  string | null;
  };
  principle: {
    id:   string;
    text: string;
  };
}

/** GET /api/corpus/kb/argomenti picker rows. */
export interface ArgomentoPickerRow {
  id:         string;
  name_it:    string;
  sort_order: number;
}

/** GET /api/corpus/kb/argomenti/:id/principles picker rows. */
export interface PrinciplePickerRow {
  id:               string;
  argomento_id:     string;
  principle_number: number;
  name_it:          string;
  citation:         string;  // e.g. "[arg13 · P5]"
}

// ---------------------------------------------------------------------------
// W6 v1.1 Session 2 — Tab 4 / Brain payload types
// ---------------------------------------------------------------------------

export interface StudentMessageRow {
  id:           string;
  kind:         'instructor_response' | 'instructor_async' | 'flag_one_on_one_internal';
  body_md:      string;
  source_kind:  'gap_response' | 'tab4_message' | 'tab4_flag';
  source_id:    string | null;
  created_at:   string;
  delivered_at: string | null;
  archived_at:  string | null;
}

export interface PauseStudentResponse {
  student: {
    id:                          string;
    paused:                      boolean;
    paused_at:                   string | null;
    paused_by_school_staff_id:   string | null;
    paused_reason:               string | null;
  };
  already_paused?: boolean;
}

export interface BrainNoteWriteResult {
  id:               string;
  observations:     string;
  metadata:         Record<string, unknown>;
  author:           string;
  prior_version_id: string | null;
  created_at:       string;
  deleted_at:       string | null;
}

// ---------------------------------------------------------------------------
// Dashboard payload types (mirror src/gateway/staff-dashboard.ts on BE)
// ---------------------------------------------------------------------------

export interface DashboardHomePayload {
  studenti: {
    total:        number;
    activeLast7d: number;
  };
  prontiAllEsame: { count: number };
  aRischio:       { count: number };
  polsoClasse: {
    weak:   Array<{ argomento_id: string; name_it: string; mastery: number }>;
    strong: Array<{ argomento_id: string; name_it: string; mastery: number }>;
  };
  lacuneContenuti: { count: number };
  noteNuove:       { count: number };
}

export interface StudentProgressoPayload {
  student: {
    id:          string;
    first_name:  string;
    cohort_name: string | null;
    exam_date:   string | null;
  };
  perArgomentoMastery: Array<{
    argomento_id:        string;
    name_it:             string;
    sort_order:          number;
    average_mastery:     number;
    principles_total:    number;
    principles_observed: number;
    principles_mastered: number;
    readinessPct:        number;
    evidenceLabel:       'Non ancora osservato' | 'Pochi dati' | 'Da rinforzare' | 'In consolidamento' | 'Buona base';
    weaknessReason:      'observed_errors' | 'low_coverage' | 'mixed' | 'none';
  }>;
  currentIndice: {
    indice:          number;
    correctCount:    number;
    totalPrinciples: number;
  };
  blindSimulationHistory: Array<{
    session_id:   string;
    completed_at: string;
    correct:      number;
    total:        number;
    passed:       boolean;
  }>;
  sessionDays30: {
    answered_questions_30d: number;
    distinct_days_30d:      number;
  };
}

export interface BrainNote {
  id:               string;
  observations:     string;
  metadata:         Record<string, unknown>;
  author:           string;
  actor_kind:       'tutor' | 'instructor' | 'system' | 'unknown';
  prior_version_id: string | null;
  created_at:       string;
}

export interface StudentBrainPayload {
  student: {
    id:         string;
    first_name: string;
  };
  notes: BrainNote[];
}

export interface AttivitaSessionRow {
  session_id:       string;
  mode:             string;
  started_at:       string;
  ended_at:         string | null;
  duration_minutes: number | null;
  answered_count:   number;
  correct_count:    number;
  metadata:         Record<string, unknown>;
}

export interface AttivitaLezioneRow {
  session_id:     string;
  mode:           'fast' | 'deep';
  argomento_id:   string;
  argomento_name: string | null;
  status:         string;
  started_at:     string;
  completed_at:   string | null;
  turns:          number;
}

export interface AttivitaContentGap {
  id:          string;
  description: string;
  kind:        string;
  status:      string;
  context:     Record<string, unknown>;
  created_at:  string;
}

export interface StudentAttivitaPayload {
  student: {
    id:         string;
    first_name: string;
  };
  sessions:        AttivitaSessionRow[];
  lezioneSessions: AttivitaLezioneRow[];
  contentGaps:     AttivitaContentGap[];
  pagination: {
    page:      number;
    page_size: number;
    has_more:  boolean;
  };
}

// W6 v1.1 Session 3 (item 12) — 30-day Indice trend chart payload.
export interface IndiceTrendPoint {
  date:   string;  // YYYY-MM-DD
  indice: number;  // 0..100
}

export interface IndiceTrendPayload {
  student: {
    id:         string;
    first_name: string;
  };
  period_days: number;
  trend:       IndiceTrendPoint[];
}

export interface StudentAttivitaSessionPayload {
  session: {
    id:         string;
    mode:       string;
    started_at: string;
    ended_at:   string | null;
  };
  events: Array<{
    sequence_number:  number;
    question_id:      string;
    question_text:    string | null;
    is_true:          boolean | null;
    student_answer:   boolean | null;
    correct_answer:   boolean | null;
    response_time_ms: number | null;
  }>;
}

// ---------------------------------------------------------------------------
// W6 v1 Session 3 — gap queue + metrics payload types
// (mirror src/gateway/staff-gap-queue.ts + src/gateway/staff-metrics.ts on BE)
// ---------------------------------------------------------------------------

export interface GapQueueRow {
  id:                  string;
  description:         string;
  kind:                string;
  status:              string;
  context:             Record<string, unknown>;
  student_id:          string | null;
  student_first_name:  string | null;
  student_last_name:   string | null;
  retrieval_attempt_summary: string | null;
  created_at:          string;
  resolved_at:         string | null;
  resolved_by_school_staff_id: string | null;
  /** First name of the staff who acted on this gap, when present + visible
   *  to the caller's school. Used to render "Risposta di Marco: …" on
   *  resolved rows. Null for unresolved rows or cross-school staff. */
  resolved_by_first_name: string | null;
  /** Last name of the staff who acted on this gap, when present + visible. */
  resolved_by_last_name:  string | null;
}

export interface GapQueuePayload {
  gaps:       GapQueueRow[];
  pagination: {
    page:      number;
    page_size: number;
    has_more:  boolean;
  };
}

export interface RefusalRatePayload {
  rate:           number;   // 0..1
  refused_count:  number;
  total_count:    number;
  period_days:    number;
  target_rate:    number;
  /** FP9 — mean cost_usd across the window's Chiedi turns (real spend signal). */
  avg_cost_usd?:  number;
  /** FP9 — total Chiedi turn count in the window. */
  turn_count?:    number;
}

export interface CostPerStudentPayload {
  avg_cost_usd:   number;
  total_cost_usd: number;
  p50_cost_usd:   number;
  p95_cost_usd:   number;
  student_count:  number;
  session_count:  number;
  period_days:    number;
  breakdown_by_variant: Array<{
    variant:        string;
    count:          number;
    mean_cost_usd:  number;
    p50_cost_usd:   number;
    p95_cost_usd:   number;
    passes_target:  boolean;
  }>;
}

export interface InstructorTimePayload {
  avg_minutes_per_student: number;
  total_minutes:           number;
  student_count:           number;
  read_event_count:        number;
  period_days:             number;
  expected_zero_until:     string | null;
}

// ---------------------------------------------------------------------------
// W6 v1.1 — class pulse + alerts payload types
// (mirror src/gateway/staff-pulse.ts + src/gateway/staff-alerts.ts on BE)
// ---------------------------------------------------------------------------

export interface PulseHeatmapCell {
  cohort_id:     string;
  cohort_label:  string;
  mastery_avg:   number;
  sample_size:   number;
}

export interface PulseHeatmapRow {
  argomento_id:   string;
  argomento_name: string;
  sort_order:     number;
  cells:          PulseHeatmapCell[];
}

export interface PulseWeeklyDelta {
  argomento_id:    string;
  argomento_name:  string;
  cohort_id:       string;
  cohort_label:    string;
  current_avg:     number;
  previous_avg:    number;
  delta:           number;
}

export interface PulseOutlier {
  argomento_id:    string;
  argomento_name:  string;
  cohort_id:       string;
  cohort_label:    string;
  mastery_avg:     number;
  median_in_arg:   number;
  z_score:         number;
}

export interface PulsePayload {
  period_days:     number;
  heatmap:         PulseHeatmapRow[];
  weekly_deltas:   PulseWeeklyDelta[];
  outliers:        PulseOutlier[];
  planning_hint:   string | null;
}

export type AlertKind = 'at_risk_daily' | 'gap_backlog_weekly' | 'urgent_event' | 'monday_cohort';

export interface AlertRow {
  id:                       string;
  alert_kind:               AlertKind;
  school_id:                string;
  target_school_staff_id:   string | null;
  payload:                  Record<string, unknown>;
  last_sent_at:             string | null;
  last_seen_at:             string | null;
  created_at:               string;
}

export interface AlertsRecentPayload {
  alerts: AlertRow[];
}
